// lib/workstations/config-schema.js
// ═══════════════════════════════════════════════════════════
// WORKSTATION CONFIG SCHEMA
// ═══════════════════════════════════════════════════════════
// Single source of truth for the JSONB blob stored in
// workstations.config. Every tab reads its slice from here.
//
// Structure (locked):
//   config = {
//     identity:    { ... }         — name, domain, instruments, cadence
//     criteria:    { ... }         — rules, thresholds, studies/types
//     tab_config:  { ... }         — per-tab behavior
//   }
//
// Rule: every field added here must be backwards compatible.
// Old workstations with missing fields should render correctly
// by falling back to sensible defaults (see getDefaults below).
// ═══════════════════════════════════════════════════════════

export const DEFAULT_CONFIG = {
  identity: {
    workstation_name: '',
    domain_classification: '',
    primary_instruments: [],
    session_cadence: 'daily',
  },
  criteria: {
    hard_rules: [],
    process_checklist: [],
    qualifying_criteria: [],
    disqualifying_criteria: [],
  },
  tab_config: {
    dashboard:  { sections: [] },
    // think — replaces chat; reads as "deep work with Shift"
    think:      { system_prompt_extends: null, voice: 'default' },
    // knowledge — absorbs the old "learn" slot; curriculum_source
    // lives alongside linked_domain
    knowledge:  { linked_domain: null, curriculum_source: null },
    // experience — your trade journal (what you DID)
    experience: { observation_types: [], work_types: [] },
    // findings — AI's analysis of your trading (Pattern Library
    // + Signal Matrix + Deep Analysis on Demand). Detection
    // threshold is the # of trades before the Pattern Library
    // unlocks full statistical patterns (default 14).
    findings:   { detection_threshold: 14 },
    // signal_read — upload market-moving documents (FOMC, Fed
    // speech, earnings call, economic data); returns the
    // dual-direction output (Market Direction / Your Direction
    // / Gap / Watch List / Ignore) per the Platform Architecture
    // doc. Per-domain document type list controls the
    // Practitioner Lens routing.
    signal_read: { document_types: [], practitioner_lens_enabled: true },
    // watchlist — replaces pipeline; Long-Term 10x overrides
    // the label to "Companies" via tab_config.watchlist.label
    watchlist:  { enabled: false, label: 'Watchlist', entity_type: null },
    // resources — merges uploads + connections: files I brought
    // in + integrations I connected; one inventory surface
    resources:  { upload_categories: [], available_connections: [] },
    // catalyst — Catalyst Research methodology surface (penny +
    // catalyst sustain-vs-fade). Long-Term 10x ONLY (gated by
    // isTabEnabled). 5 sections: open predictions, hypotheses,
    // track record, new prediction, methodology.
    catalyst:   { methodology_version: 'catalyst-v2.0' },
  },
};

/**
 * Merge a partial config with defaults so the UI never has to
 * defensively check every field.
 */
export function resolveConfig(config) {
  if (!config || typeof config !== 'object') return clone(DEFAULT_CONFIG);
  return deepMerge(clone(DEFAULT_CONFIG), config);
}

/**
 * Read a single tab's config slice with defaults applied.
 */
export function tabConfigFor(config, tabKey) {
  const resolved = resolveConfig(config);
  return resolved.tab_config?.[tabKey] || DEFAULT_CONFIG.tab_config[tabKey] || {};
}

/**
 * Check whether a given tab should be enabled for this
 * workstation. Some tabs (Pipeline) are explicitly toggleable;
 * the rest default to enabled.
 */
export function isTabEnabled(config, tabKey, workstation) {
  const slice = tabConfigFor(config, tabKey);
  // Watchlist is the only explicitly toggleable tab — domains
  // without trackable entities (e.g. a writing workstation) can
  // hide it by setting enabled: false.
  if (tabKey === 'watchlist') return slice.enabled === true;
  // QQQ tab — DAY-TRADING-WORKSTATIONS-ONLY (Commit 19).
  // Defensive matching: slug exact OR name-contains, since slug
  // generation may vary across workstation creation paths.
  if (tabKey === 'qqq') {
    const slug = (workstation?.slug || '').toLowerCase();
    const name = (workstation?.name || '').toLowerCase();
    return slug === 'day-trading' || slug === 'qqq' || name.includes('day trading');
  }
  // Catalyst tab — LONG-TERM-10X-ONLY. Defensive matching across
  // both slug and name since slug generation may vary across
  // workstation creation paths.
  if (tabKey === 'catalyst') {
    const slug = (workstation?.slug || '').toLowerCase();
    const name = (workstation?.name || '').toLowerCase();
    return slug.includes('long-term') || slug.includes('10x') ||
           name.includes('long-term') || name.includes('10x');
  }
  // Deals + Close — WEALTH-BUILDING-WORKSTATIONS-ONLY for now (Close is
  // designed to be universal; gated here while it proves out). Defensive
  // matching across domain_key, slug, and name.
  if (tabKey === 'deals' || tabKey === 'close' || tabKey === 'tools') {
    const domain = (workstation?.domain_key || '').toLowerCase();
    const slug = (workstation?.slug || '').toLowerCase();
    const name = (workstation?.name || '').toLowerCase();
    return domain === 'wealth-building' || domain === 'wealth' ||
           slug.includes('wealth') || slug.includes('real-estate') || slug.includes('compound') ||
           name.includes('wealth') || name.includes('real estate') || name.includes('compound');
  }
  return true;
}

// ── Helpers ──────────────────────────────────────────────
function clone(obj) { return JSON.parse(JSON.stringify(obj)); }

function deepMerge(target, source) {
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      target[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}
